import React, { useEffect, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled, { keyframes } from 'styled-components';
import {
  FaSatelliteDish,
  FaCheckCircle,
  FaExclamationTriangle,
  FaPlayCircle,
  FaClock,
  FaDatabase,
} from 'react-icons/fa';
import { listScrapeRuns } from '../../services/competitorsService';

const mono = "'JetBrains Mono', 'Consolas', monospace";
const accent = '#00B4D8';
const borderSoft = 'rgba(255, 255, 255, 0.06)';

const Panel = styled.section`
  position: relative;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.012), transparent 35%),
    var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  padding: 1.25rem;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin: 0 0 1rem;
  padding: 0 0 0.6rem;
  border-bottom: 1px solid ${borderSoft};
`;

const Title = styled.h3`
  margin: 0;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.55rem;

  svg { color: ${accent}; }
`;

const Pill = styled.span`
  margin-left: auto;
  font-family: ${mono};
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${accent};
  border: 1px solid rgba(0, 180, 216, 0.35);
  background: rgba(0, 180, 216, 0.08);
  padding: 2px 8px;
  border-radius: 3px;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.5rem;
  margin-bottom: 1.1rem;
`;

const StatTile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${borderSoft};
  border-radius: 4px;
  background: rgba(11, 19, 32, 0.5);
`;

const StatLabel = styled.span`
  font-family: ${mono};
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
  display: flex;
  align-items: center;
  gap: 0.35rem;

  svg { font-size: 0.7rem; color: ${accent}; }
`;

const StatValue = styled.span`
  font-family: ${mono};
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const StatSub = styled.span`
  font-family: ${mono};
  font-size: 0.7rem;
  color: var(--text-disabled);
`;

const TableWrap = styled.div`
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  overflow-x: auto;

  &::-webkit-scrollbar { height: 6px; }
  &::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.08); border-radius: 6px; }
`;

const Table = styled.table`
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 0.6rem 0.85rem;
  text-align: left;
  font-family: ${mono};
  font-size: 0.62rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
  font-weight: 500;
  background: rgba(255, 255, 255, 0.025);
  border-bottom: 1px solid ${borderSoft};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 0.6rem 0.85rem;
  border-bottom: 1px solid ${borderSoft};
  color: var(--text-secondary);
  font-size: 0.85rem;
  font-family: ${({ $mono }) => ($mono ? mono : 'inherit')};
  vertical-align: top;
`;

const Tr = styled.tr`
  &:hover { background: rgba(255, 255, 255, 0.02); }
  &:last-child td { border-bottom: none; }
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-family: ${mono};
  font-size: 0.65rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 3px;
  border: 1px solid;

  ${({ status }) => {
    if (status === 'ok')
      return `color: var(--alert-success); border-color: rgba(6, 214, 160, 0.4); background: rgba(6, 214, 160, 0.08);`;
    if (status === 'error')
      return `color: var(--alert-error); border-color: rgba(239, 71, 111, 0.4); background: rgba(239, 71, 111, 0.08);`;
    return `color: ${accent}; border-color: rgba(0, 180, 216, 0.35); background: rgba(0, 180, 216, 0.08);`;
  }}
`;

const Empty = styled.div`
  padding: 1rem;
  text-align: center;
  color: var(--text-disabled);
  font-size: 0.9rem;
`;

const ErrorBox = styled(Empty)`
  color: var(--alert-error);
`;

const spin = keyframes`to { transform: rotate(360deg); }`;
const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 1.2rem;
  color: var(--text-disabled);
  font-family: ${mono};
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    border: 2px solid rgba(0, 180, 216, 0.25);
    border-top-color: ${accent};
    border-radius: 50%;
    animation: ${spin} 0.9s linear infinite;
  }
`;

const Footer = styled.div`
  margin-top: 1rem;
  padding-top: 0.85rem;
  border-top: 1px solid ${borderSoft};
  font-family: ${mono};
  font-size: 0.7rem;
  letter-spacing: 0.04em;
  color: var(--text-disabled);
  line-height: 1.55;
`;

function fmt(value, options = {}) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d)) return '—';
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  });
}

function durationLabel(start, end) {
  if (!start || !end) return '—';
  const ms = new Date(end) - new Date(start);
  if (isNaN(ms) || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

const MonitoringPanel = () => {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, error: null, data: null });
    listScrapeRuns(20)
      .then((data) => {
        if (!cancelled) setState({ loading: false, error: null, data });
      })
      .catch((err) => {
        if (!cancelled)
          setState({
            loading: false,
            error: friendlyError(err, 'Error'),
            data: null,
          });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const runs = state.data?.runs || [];
  const totals = state.data?.totals;
  const schedule = state.data?.schedule;
  const lastRun = runs[0];

  return (
    <Panel>
      <Header>
        <Title>
          <FaSatelliteDish /> Monitoreo automático de competidores
        </Title>
        <Pill>
          {schedule?.cron ? `programado · ${schedule.cron}` : 'programado'}
        </Pill>
      </Header>

      <StatsRow>
        <StatTile>
          <StatLabel><FaPlayCircle /> Última corrida</StatLabel>
          <StatValue style={{ fontSize: '0.9rem' }}>
            {lastRun ? fmt(lastRun.started_at) : '—'}
          </StatValue>
          {lastRun?.status && (
            <StatSub>
              <StatusChip status={lastRun.status}>
                {lastRun.status === 'ok' ? <FaCheckCircle /> : <FaExclamationTriangle />}
                {lastRun.status}
              </StatusChip>
            </StatSub>
          )}
        </StatTile>
        <StatTile>
          <StatLabel><FaClock /> Próxima corrida</StatLabel>
          <StatValue style={{ fontSize: '0.9rem' }}>
            {schedule?.next_run_at ? fmt(schedule.next_run_at) + ' UTC' : '—'}
          </StatValue>
          <StatSub>{schedule?.cron}</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel><FaDatabase /> Notas indexadas</StatLabel>
          <StatValue>{totals?.articles ?? '—'}</StatValue>
          <StatSub>{totals?.competitors ?? 0} competidores</StatSub>
        </StatTile>
        <StatTile>
          <StatLabel>Notas última corrida</StatLabel>
          <StatValue>
            {lastRun ? `+${lastRun.articles_added}` : '—'}
          </StatValue>
          <StatSub>
            {lastRun ? `${lastRun.competitors_processed} procesados` : ''}
          </StatSub>
        </StatTile>
      </StatsRow>

      {state.loading && <Loading>Cargando runs</Loading>}
      {state.error && <ErrorBox>No se pudo cargar · {state.error}</ErrorBox>}
      {!state.loading && !state.error && runs.length === 0 && (
        <Empty>Aún no hay corridas registradas.</Empty>
      )}

      {!state.loading && !state.error && runs.length > 0 && (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>Run</Th>
                <Th>Inicio</Th>
                <Th>Duración</Th>
                <Th>Status</Th>
                <Th>Procesados</Th>
                <Th>Agregados</Th>
                <Th>Trigger</Th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <Tr key={r.id}>
                  <Td $mono>#{r.id}</Td>
                  <Td $mono>{fmt(r.started_at)}</Td>
                  <Td $mono>{durationLabel(r.started_at, r.finished_at)}</Td>
                  <Td>
                    <StatusChip status={r.status}>
                      {r.status === 'ok'
                        ? <FaCheckCircle />
                        : r.status === 'error'
                          ? <FaExclamationTriangle />
                          : null}
                      {r.status}
                    </StatusChip>
                  </Td>
                  <Td $mono>{r.competitors_processed}</Td>
                  <Td $mono style={{ color: r.articles_added > 0 ? accent : 'var(--text-disabled)' }}>
                    +{r.articles_added}
                  </Td>
                  <Td $mono>{r.triggered_by || '—'}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      <Footer>
        Disparo manual:&nbsp;
        <span style={{ color: 'var(--text-secondary)' }}>
          curl -X POST -H "Authorization: Bearer $CRON_SECRET" {'<host>'}/api/cron/refresh-competitors
        </span>
      </Footer>
    </Panel>
  );
};

export default MonitoringPanel;
